import { AnimatePresence, motion } from "framer-motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ContentTypeCombobox } from "@/components/generate/ContentTypeCombobox";
import { CaretDown, CaretUp, SlidersHorizontal, Warning } from "@phosphor-icons/react";

const LABEL_CLS = "block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2";

export const GenerateConfigFields = ({
  contentType, setContentType, isMultiSection, contentTypeOpen, setContentTypeOpen,
  platform, setPlatform, toneVariations, toggleTone,
  advancedOpen, setAdvancedOpen,
  audience, setAudience, goal, setGoal,
  cta, setCta, offerAsset, setOfferAsset,
  lengthConstraint, setLengthConstraint,
  aiModel, setAiModel, availableModels,
  PLATFORMS, TONES, GOALS,
}) => {
  return (
    <>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ContentTypeCombobox contentType={contentType} onSelect={setContentType} isMultiSection={isMultiSection} open={contentTypeOpen} onOpenChange={setContentTypeOpen} />
        <div>
          <label className={LABEL_CLS}>Platform <span className="text-[#FF4400]">*</span></label>
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="select-platform"><SelectValue placeholder="Select platform" /></SelectTrigger>
            <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
              {PLATFORMS.map((p) => (<SelectItem key={p} value={p} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{p}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </motion.div>

      {/* Tone */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <label className={`${LABEL_CLS} mb-3`}>Tone Variation <span className="text-[#FF4400]">*</span></label>
        <div className="flex flex-nowrap overflow-x-auto gap-2 md:flex-wrap md:overflow-visible pb-2 md:pb-0 no-scrollbar">
          {TONES.map((tone) => (
            <button key={tone} type="button" onClick={() => toggleTone(tone)} className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${toneVariations.includes(tone) ? "bg-[var(--accent-subtle)] border-[#FF4400] text-[#FF4400]" : "bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[#FF4400]/30 hover:text-[var(--text-primary)]"}`} data-testid={`tone-${tone.toLowerCase()}`}>
              {tone}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Advanced Options Toggle */}
      <div>
        <button type="button" onClick={() => setAdvancedOpen(!advancedOpen)} className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-[var(--bg-card)] border border-[var(--border-strong)] text-sm font-medium text-[var(--text-secondary)] transition-colors hover:border-[#FF4400]/30 group" data-testid="advanced-options-toggle">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={16} className="text-[var(--text-muted)] group-hover:text-[#FF4400] transition-colors" />
            <span>Advanced Options</span>
            {!advancedOpen && (audience || goal || cta || offerAsset || lengthConstraint || aiModel) && (
              <span className="text-xs text-[var(--text-muted)] truncate max-w-[280px] hidden sm:inline">{[audience && "Audience", goal && "Goal", aiModel && "Model", cta && "CTA"].filter(Boolean).join(", ")}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!advancedOpen && (audience || goal || aiModel) && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FF4400]/10 text-[#FF4400] font-semibold">{[audience, goal, cta, offerAsset, lengthConstraint, aiModel].filter(Boolean).length} set</span>
            )}
            {advancedOpen ? <CaretUp size={16} className="text-[var(--text-muted)]" /> : <CaretDown size={16} className="text-[var(--text-muted)]" />}
          </div>
        </button>
      </div>

      {/* Advanced fields */}
      <AnimatePresence>
      {advancedOpen && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className={LABEL_CLS}>Audience <span className="text-[#FF4400]">*</span></label>
            <Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Who is this for? e.g., B2B SaaS founders, agency owners, solo consultants" maxLength={255} required className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] h-11" data-testid="input-audience" />
          </div>
          <div>
            <label className={LABEL_CLS}>Goal <span className="text-[#FF4400]">*</span></label>
            <Select value={goal} onValueChange={setGoal}>
              <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="select-goal"><SelectValue placeholder="Select goal" /></SelectTrigger>
              <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">{GOALS.map((g) => (<SelectItem key={g} value={g} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{g}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <label className={LABEL_CLS}>CTA</label>
            <Input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="e.g., Book a strategy call, Download the guide" maxLength={500} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] h-11" data-testid="input-cta" />
          </div>
          <div className="sm:col-span-2">
            <label className={LABEL_CLS}>Offer or Asset Mentioned</label>
            <Input value={offerAsset} onChange={(e) => setOfferAsset(e.target.value)} placeholder="e.g., Free strategy session, The Growth Playbook, 30-min audit" maxLength={500} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] h-11" data-testid="input-offer-asset" />
          </div>
          <div>
            <label className={LABEL_CLS}>Length Constraint</label>
            <Input value={lengthConstraint} onChange={(e) => setLengthConstraint(e.target.value)} placeholder="e.g., 300 words, 2000 characters" maxLength={50} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] h-11" data-testid="input-length" />
          </div>
          <div>
            <label className={LABEL_CLS}>AI Model <span className="text-[#FF4400]">*</span></label>
            {availableModels.length > 0 ? (
              <Select value={aiModel} onValueChange={setAiModel}>
                <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="select-ai-model"><SelectValue placeholder="Select model" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">{availableModels.map((m) => (<SelectItem key={m} value={m} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{m}</SelectItem>))}</SelectContent>
              </Select>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-[var(--bg-raised)] border border-[#FF4400]/20 text-sm text-[#FF4400]" data-testid="no-models-warning"><Warning size={16} /><span>Configure at least one AI provider in Settings</span></div>
            )}
          </div>
        </div>
        </motion.div>
      )}
      </AnimatePresence>
    </>
  );
};
